/**
 * SellerPackPage — Australian "how to sell a house" disclosure helper.
 *
 * Enter postcode → returns state, mandatory forms, optional forms,
 * cost estimate and the tradespeople you'll need to engage.  Each
 * form expands to show its plain-English Grade-7 tutorial.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Search, MapPin, FileText, ChevronDown, ChevronUp, ExternalLink,
  AlertTriangle, Wrench, DollarSign, Home, Droplet, Building2,
  CheckCircle2, BookOpen, Loader2,
} from "lucide-react";
import "./SellerPackPage.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

export default function SellerPackPage() {
  const [postcode, setPostcode] = useState("");
  const [hasPool, setHasPool] = useState(false);
  const [isStrata, setIsStrata] = useState(false);
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openForm, setOpenForm] = useState(null);
  const [formDetail, setFormDetail] = useState({});
  const [states, setStates] = useState([]);

  useEffect(() => {
    api.get(`/api/seller-pack/states`)
       .then(r => setStates(r.data || []))
       .catch(() => { /* offline-friendly */ });
  }, []);

  const lookup = async (e) => {
    e?.preventDefault?.();
    if (!postcode || postcode.length < 3) return;
    setLoading(true); setError(""); setPack(null);
    try {
      const r = await api.get(
        `/api/seller-pack/by-postcode/${postcode}`,
        { params: { has_pool: hasPool, is_strata: isStrata }, timeout: 30000 });
      setPack(r.data);
      try {
        api.post(`/api/seller-pack/track-view`,
                 { postcode, state: r.data.state });
      } catch (_) { /* ignore tracking errors */ }
    } catch (e2) {
      setError(e2?.response?.data?.detail ||
               "Couldn't find that postcode. Use a 4-digit Australian one.");
    }
    setLoading(false);
  };

  const toggleForm = async (form) => {
    if (openForm === form.id) { setOpenForm(null); return; }
    setOpenForm(form.id);
    if (!formDetail[form.id]) {
      try {
        const r = await api.get(`/api/seller-pack/form/${form.id}`,
                                { timeout: 20000 });
        setFormDetail(d => ({ ...d, [form.id]: r.data }));
      } catch (_) { /* ignore */ }
    }
  };

  return (
    <div className="sp" data-testid="seller-pack-root">
      {/* Hero */}
      <header className="sp-hero">
        <span className="sp-hero__eyebrow">
          <FileText size={14}/> Hustle Property · Seller Compliance Helper
        </span>
        <h1>Sell your home, the legal way.</h1>
        <p className="sp-hero__lede">
          Since <strong>1 August 2025</strong>, Queensland sellers must give
          buyers a Form 2 Seller Disclosure Statement <em>before</em> they sign —
          and every other Aussie state has its own rules. Enter your postcode
          and we&apos;ll show you exactly which forms, inspections and tradies
          you need. Written in plain English so a Year 7 can follow it.
        </p>
      </header>

      {/* Postcode lookup */}
      <form className="sp-lookup" onSubmit={lookup} data-testid="sp-lookup-form">
        <div className="sp-lookup__field">
          <Search size={16}/>
          <input
            type="text" inputMode="numeric" pattern="\d{4}" maxLength={4}
            placeholder="Enter your postcode (e.g. 4000)"
            value={postcode}
            onChange={(e) => setPostcode(e.target.value.replace(/\D/g, ""))}
            data-testid="sp-postcode"
            required
          />
        </div>
        <label className="sp-lookup__check">
          <input type="checkbox" checked={hasPool}
                 onChange={(e) => setHasPool(e.target.checked)}
                 data-testid="sp-has-pool"/>
          <Droplet size={14}/> Property has a pool
        </label>
        <label className="sp-lookup__check">
          <input type="checkbox" checked={isStrata}
                 onChange={(e) => setIsStrata(e.target.checked)}
                 data-testid="sp-is-strata"/>
          <Building2 size={14}/> Strata / unit / townhouse
        </label>
        <button type="submit" className="sp-lookup__btn"
                disabled={loading || postcode.length !== 4}
                data-testid="sp-lookup-btn">
          {loading ? <Loader2 size={14} className="sp-spin"/> : <Search size={14}/>}
          {loading ? "Looking up…" : "Get my checklist →"}
        </button>
      </form>

      {error && (
        <div className="sp-error" data-testid="sp-error">
          <AlertTriangle size={16}/> {error}
        </div>
      )}

      {/* Quick-state reference */}
      {!pack && states.length > 0 && (
        <section className="sp-states" data-testid="sp-states">
          <h2><MapPin size={16}/> The disclosure regime, state by state</h2>
          <div className="sp-states__grid">
            {states.map(s => (
              <div key={s.state} className="sp-states__card">
                <strong>{s.state}</strong>
                <span>{s.headline_form}</span>
                <small>
                  {s.mandatory_count} mandatory · since {s.since}
                </small>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Results */}
      {pack && (
        <section className="sp-results" data-testid="sp-results">
          <div className="sp-results__head">
            <div>
              <span className="sp-results__state">{pack.state}</span>
              <h2>
                Postcode {pack.postcode} —{" "}
                <span className="sp-results__count">
                  {pack.mandatory_forms.length} mandatory item
                  {pack.mandatory_forms.length === 1 ? "" : "s"}
                </span>
              </h2>
              <p className="sp-results__cost">
                <DollarSign size={14}/> Estimated total compliance cost:
                <strong> ${pack.cost_estimate_aud.low.toLocaleString()} – ${pack.cost_estimate_aud.high.toLocaleString()}</strong>
                {" "}({pack.has_pool ? "with pool" : "no pool"},{" "}
                {pack.is_strata ? "strata" : "freehold"})
              </p>
            </div>
          </div>

          <div className="sp-callout">
            <strong>Next step →</strong> {pack.next_step}
          </div>

          <a
            className="sp-pdf-btn"
            href={`${API}/api/seller-pack/by-postcode/${pack.postcode}/pdf?has_pool=${pack.has_pool}&is_strata=${pack.is_strata}`}
            data-testid="sp-pdf-download"
            target="_blank"
            rel="noopener"
          >
            <FileText size={14}/> Download personalised PDF checklist
            <small>Members only — sign in or create a free account.</small>
          </a>

          {/* Mandatory forms */}
          <h3 className="sp-section-h">
            <CheckCircle2 size={16}/> Mandatory — you cannot legally sell without these
          </h3>
          <ul className="sp-forms">
            {pack.mandatory_forms.map(f => (
              <FormCard
                key={f.id}
                form={f}
                detail={formDetail[f.id]}
                isOpen={openForm === f.id}
                onToggle={() => toggleForm(f)}
              />
            ))}
          </ul>

          {/* Optional forms */}
          {pack.optional_forms.length > 0 && (
            <>
              <h3 className="sp-section-h sp-section-h--optional">
                <FileText size={16}/> Optional but strongly recommended
              </h3>
              <ul className="sp-forms">
                {pack.optional_forms.map(f => (
                  <FormCard
                    key={f.id}
                    form={f}
                    detail={formDetail[f.id]}
                    isOpen={openForm === f.id}
                    onToggle={() => toggleForm(f)}
                  />
                ))}
              </ul>
            </>
          )}

          {/* Tradespeople */}
          <h3 className="sp-section-h">
            <Wrench size={16}/> Tradies &amp; professionals you&apos;ll need
          </h3>
          <div className="sp-trades">
            {pack.tradespeople.map(t => (
              <div key={t.id} className="sp-trade" data-testid={`sp-trade-${t.id}`}>
                <div className="sp-trade__head">
                  <Wrench size={14}/>
                  <strong>{t.label}</strong>
                  <span className="sp-trade__cost">{t.typical_cost}</span>
                </div>
                <p>{t.what_they_do}</p>
                <small>Licensed by: {t.licensed_by}</small>
                <a className="sp-trade__find"
                   href={`/trades?type=${t.id}&postcode=${pack.postcode}`}
                   data-testid={`sp-trade-find-${t.id}`}>
                  Find one near {pack.postcode} →
                </a>
              </div>
            ))}
          </div>

          <footer className="sp-disclaimer">
            <BookOpen size={13}/>
            <span>
              General information only, current as of February 2026.
              Always confirm with your state regulator or a licensed
              conveyancer before signing. Hustle Group Pty Ltd takes no
              responsibility for decisions made from this page.
            </span>
          </footer>
        </section>
      )}
    </div>
  );
}


function FormCard({ form, detail, isOpen, onToggle }) {
  return (
    <li className="sp-form" data-testid={`sp-form-${form.id}`}>
      <button
        type="button"
        className="sp-form__head"
        onClick={onToggle}
        aria-expanded={isOpen}
        data-testid={`sp-form-toggle-${form.id}`}
      >
        <div className="sp-form__head-text">
          <strong>{form.short_name}</strong>
          <span className="sp-form__appliesto">
            {form.applies_to === "pool"   ? "Pool only " :
             form.applies_to === "strata" ? "Strata only " :
             form.applies_to === "house"  ? "Houses " : ""}
            · {form.cost_aud}
          </span>
          <p>{form.plain_summary}</p>
        </div>
        {isOpen ? <ChevronUp size={18}/> : <ChevronDown size={18}/>}
      </button>

      {isOpen && (
        <div className="sp-form__body" data-testid={`sp-form-body-${form.id}`}>
          {form.penalty && (
            <div className="sp-form__penalty">
              <AlertTriangle size={13}/> <strong>If you skip it:</strong> {form.penalty}
            </div>
          )}

          <h5>Step-by-step (anyone can do it):</h5>
          {!detail ? (
            <div className="sp-form__loading">
              <Loader2 size={14} className="sp-spin"/> Loading tutorial…
            </div>
          ) : (
            <ol className="sp-form__steps">
              {detail.plain_steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          )}

          <div className="sp-form__links">
            <a href={form.official_url} target="_blank" rel="noopener noreferrer"
               data-testid={`sp-form-official-${form.id}`}>
              <ExternalLink size={12}/> Official source
            </a>
            <a href={form.help_url} target="_blank" rel="noopener noreferrer"
               data-testid={`sp-form-help-${form.id}`}>
              <BookOpen size={12}/> Help guide
            </a>
            <small className="sp-form__provider">
              <Home size={11}/> Provided by: {form.who_provides}
            </small>
          </div>
        </div>
      )}
    </li>
  );
}
